// The site's characters at this table (issue 848), as the client calls them.
// The server reads the placements and the sheets from the site; the client
// only asks, through `requestJson` (and so `authFetch`), and says what it is
// told. A refusal carries the server's own sentence, shown as it is.
import type { PartyTag } from '@dragons-table/vtt-shared';
import { requestJson } from './api';
import { siteUrl } from './session';

export interface TableCharacter {
  id: string;
  name: string;
  owner: { id: string; handle: string | null };
  /** The tokens on the scene asked about that stand for this character. */
  linked: string[];
  /** The parties it is placed in (issue 1579), oldest first; absent from an older server. */
  parties?: PartyTag[];
}

export type CharacterAnswer =
  | { kind: 'ok'; id: string; notice?: string; changed?: boolean }
  | { kind: 'refused'; reason: string }
  | { kind: 'error' };

/** The site's own page for a character. */
export const characterPageUrl = (id: string): string => `${siteUrl()}/characters?id=${encodeURIComponent(id)}`;

/**
 * The second window a table's sheets open in (issue 1053): one name per table, so
 * opening a sheet again, or another, reuses that window rather than piling up tabs.
 * The sheet stays the site's own page: the owner edits it there, the DM reads it.
 */
export const sheetWindowName = (campaignId: string): string => `tdt-sheet-${campaignId}`;

const sceneQuery = (sceneId: string | null) => (sceneId ? `?scene_id=${encodeURIComponent(sceneId)}` : '');
const withScene = (body: Record<string, unknown>, sceneId: string | null) => (sceneId ? { ...body, scene_id: sceneId } : body);

function answer(status: number, body: unknown, fallbackId = ''): CharacterAnswer {
  const b = (body ?? {}) as { id?: unknown; notice?: unknown; changed?: unknown; reason?: unknown };
  if (status === 200 || status === 201) {
    return {
      kind: 'ok', id: typeof b.id === 'string' ? b.id : fallbackId,
      ...(typeof b.notice === 'string' ? { notice: b.notice } : {}),
      ...(typeof b.changed === 'boolean' ? { changed: b.changed } : {}),
    };
  }
  if (status === 0) return { kind: 'error' };
  if (typeof b.reason === 'string') return { kind: 'refused', reason: b.reason };
  if (status === 404) return { kind: 'refused', reason: 'That character or token is no longer at this table.' };
  if (status === 403) return { kind: 'refused', reason: "That isn't yours to do." };
  return { kind: 'error' };
}

/** The DM's list: the characters placed live at this table, and which tokens on the scene stand for each. Null when it cannot be read. */
export async function listTableCharacters(campaignId: string, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<TableCharacter[] | null> {
  const { status, body } = await requestJson(`/api/tables/${campaignId}/characters${sceneQuery(sceneId)}`, { method: 'GET' }, fetchImpl);
  const list = (body as { characters?: unknown } | null)?.characters;
  return status === 200 && Array.isArray(list) ? (list as TableCharacter[]) : null;
}

/** A token for a character, at a point in board pixels, on the scene the DM is looking at. */
export async function placeCharacter(campaignId: string, characterId: string, at: { x: number; y: number }, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<CharacterAnswer> {
  const { status, body } = await requestJson(`/api/tables/${campaignId}/characters/${characterId}/token`, {
    method: 'POST', body: JSON.stringify(withScene({ x: Math.round(at.x), y: Math.round(at.y) }, sceneId)),
  }, fetchImpl);
  return answer(status, body);
}

/** Link a token to a character, or (null) unlink it. */
export async function linkCharacter(campaignId: string, objectId: string, characterId: string | null, sceneId: string | null, fetchImpl: typeof fetch = fetch, replace = false): Promise<CharacterAnswer> {
  // `replace` (issue 1612): the DM was asked and chose to replace the numbers typed on the token; unasked, the server refuses.
  const { status, body } = await requestJson(`/api/tables/${campaignId}/objects/${objectId}/character`, {
    method: 'PUT', body: JSON.stringify(withScene({ character_id: characterId, ...(replace ? { replace: true } : {}) }, sceneId)),
  }, fetchImpl);
  return answer(status, body, objectId);
}

/** A player links their own plain token to their own character (qa-runs 1250). */
export async function linkMyCharacter(campaignId: string, objectId: string, characterId: string, fetchImpl: typeof fetch = fetch): Promise<CharacterAnswer> {
  const { status, body } = await requestJson(`/api/tables/${campaignId}/objects/${objectId}/character/mine`, { method: 'PUT', body: JSON.stringify({ character_id: characterId }) }, fetchImpl);
  return answer(status, body, objectId);
}

/**
 * Put the token's current hit points on its character's sheet (issue 1052; #850, Tom's A): the owner's Accept of the
 * DM's change, written with their own sign-in. The owner's alone; the server says so to anyone else.
 */
export async function acceptCharacterHp(campaignId: string, objectId: string, fetchImpl: typeof fetch = fetch): Promise<CharacterAnswer> {
  const { status, body } = await requestJson(`/api/tables/${campaignId}/objects/${objectId}/character/accept`, { method: 'POST', body: JSON.stringify({}) }, fetchImpl);
  return answer(status, body, objectId);
}

/** Whether a thing in this person's projection carries hit points not on its character's sheet (issue 1052): the DM's and its owner's to see. */
export const hpOffSheetOf = (o: unknown): boolean => (o as { hpOffSheet?: unknown } | null)?.hpOffSheet === true;

/** The words for that mark, wherever a token's hit points are shown to the DM or its owner. */
export const NOT_ON_SHEET = 'not on the sheet';

/** Read the token's numbers from its character's sheet again. The DM's, or the token's owner's. */
export async function refreshCharacter(campaignId: string, objectId: string, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<CharacterAnswer> {
  const { status, body } = await requestJson(`/api/tables/${campaignId}/objects/${objectId}/character/refresh`, {
    method: 'POST', body: JSON.stringify(withScene({}, sceneId)),
  }, fetchImpl);
  return answer(status, body, objectId);
}

/** One companion row of a master's sheet (qa-runs#384): its stable row id, its name and kind, and its tokens on the scene. */
export interface TableCompanion { row: string; name: string; kind: string; linked: string[] }

/** A master's companion rows, for its player or the DM. Null when they cannot be read; a refusal's words otherwise. */
export async function listCompanions(campaignId: string, characterId: string, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<TableCompanion[] | { reason: string } | null> {
  const { status, body } = await requestJson(`/api/tables/${campaignId}/characters/${characterId}/companions${sceneQuery(sceneId)}`, { method: 'GET' }, fetchImpl);
  const b = (body ?? {}) as { companions?: unknown; reason?: unknown };
  if (status === 200 && Array.isArray(b.companions)) return b.companions as TableCompanion[];
  return typeof b.reason === 'string' ? { reason: b.reason } : null;
}

/** A token for one companion row (qa-runs#384), at a point in board pixels: the master's player's, or the DM's. */
export async function placeCompanion(campaignId: string, characterId: string, row: string, at: { x: number; y: number }, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<CharacterAnswer> {
  const { status, body } = await requestJson(`/api/tables/${campaignId}/characters/${characterId}/companions/${encodeURIComponent(row)}/token`, {
    method: 'POST', body: JSON.stringify(withScene({ x: Math.round(at.x), y: Math.round(at.y) }, sceneId)),
  }, fetchImpl);
  return answer(status, body);
}
